// shell/components/data-list-view/persistence.ts

import { PersistedViewState, StorageProvider, ViewMode } from './types';

/**
 * Default storage provider using localStorage.
 */
export const localStorageProvider: StorageProvider = {
    get(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    },
    set(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch {
            // Storage full or disabled
        }
    },
    remove(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch {
            // Storage disabled
        }
    }
};

/**
 * Load persisted view state.
 */
export function loadViewState(
    key: string,
    storage: StorageProvider
): PersistedViewState | null {
    const raw = storage.get(key);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        return validatePersistedState(parsed);
    } catch {
        return null;
    }
}

/**
 * Save view state.
 */
export function saveViewState(
    key: string,
    state: PersistedViewState,
    storage: StorageProvider
): void {
    storage.set(key, JSON.stringify(state));
}

/**
 * Validate and sanitize persisted state.
 */
function validatePersistedState(raw: unknown): PersistedViewState | null {
    if (typeof raw !== 'object' || raw === null) return null;

    const obj = raw as Record<string, unknown>;

    // Validate viewMode
    const validModes: ViewMode[] = ['table', 'grid', 'thumbs'];
    const viewMode = validModes.includes(obj.viewMode as ViewMode)
        ? (obj.viewMode as ViewMode)
        : 'table';

    return {
        viewMode,
        thumbSize: typeof obj.thumbSize === 'number' ? obj.thumbSize : undefined,
        sortColumn: typeof obj.sortColumn === 'string' ? obj.sortColumn : undefined,
        sortDirection: obj.sortDirection === 'asc' || obj.sortDirection === 'desc'
            ? obj.sortDirection
            : undefined,
        visibleColumns: Array.isArray(obj.visibleColumns)
            ? obj.visibleColumns.filter((v): v is string => typeof v === 'string')
            : undefined,
        columnOrder: Array.isArray(obj.columnOrder)
            ? obj.columnOrder.filter((v): v is string => typeof v === 'string')
            : undefined,
        heatMapEnabled: typeof obj.heatMapEnabled === 'boolean'
            ? obj.heatMapEnabled
            : undefined,
        scrollTop: typeof obj.scrollTop === 'number' ? obj.scrollTop : undefined
    };
}
